import {
  credentialSharingAgreementApi,
  CredentialSharingAuthority,
  CredentialSharingObligation,
  useSetCredentialSharingAuthorityMutation,
} from 'api/credentialSharingAgreement'
import {
  APP_STORE_CONNECT_URL,
  buildLpaSupportMailto,
  getLpaTarget,
} from 'constants/credentialSharingCta'
import useCredentialSharingNagDismissal from 'hooks/useCredentialSharingNagDismissal'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import useIsAdmin from 'hooks/useIsAdmin'
import useIsThanxAdmin from 'hooks/useIsThanxAdmin'
import { openExternal } from 'utilities/openExternal'

type UseCredentialSharingNagResult = {
  // Persistent-surface visibility (banner + entry point). Driven by the
  // endpoint's `show`, gated client-side on flag + merchant-admin.
  show: boolean
  // Modal visibility: `show` AND the merchant hasn't dismissed the nag today.
  showModal: boolean
  // Which case the modal defaults to: 'pending' -> Case 1 (grant), 'declined'
  // -> Case 2 (self-serve). 'granted' never surfaces (show would be false).
  authority?: CredentialSharingAuthority
  // Blocking obligations to render one row per (membership vs license_agreement).
  obligations: CredentialSharingObligation[]
  dismiss: () => void
  // "Let Thanx handle it" — link out to the LPA signing flow, or fall back to
  // contacting support when no signing URL is configured.
  handleGrantAuthority: () => void
  // Self-serve — open App Store Connect (membership renewal + agreement both).
  handleManageOnApple: () => void
  // "I'll manage Apple myself" — persist the decline so the modal stops
  // re-pitching Thanx and defaults to the self-serve view on future visits.
  handleDecline: () => void
  // "Actually, let Thanx handle it" — reset to pending (reconsider), so the
  // grant pitch surfaces again.
  handleReconsider: () => void
}

/**
 * Manages the credential-sharing (Apple Developer account access) nag lifecycle.
 *
 * This is about authorizing Thanx to act on the merchant's Apple Developer
 * account (App Store Connect / LPA) — NOT about customer data. The decision of
 * whether and how to prompt is driven by the endpoint (`show`, `authority`,
 * `obligations`); this hook only adds the client-side gates (feature flag in
 * 'nag' mode, and the user being the merchant's own admin — not a Thanx admin
 * impersonating) and the once-per-day dismissal.
 */
export default function useCredentialSharingNag(): UseCredentialSharingNagResult {
  const flag = useFlag('mx-credential-sharing-agreement', 'off')
  const merchant = useCurrentMerchant()
  const isAdmin = useIsAdmin()
  // Thanx admins are not the responsible party for the merchant's agreement.
  // Treat the user as the merchant's responsible admin only when they admin
  // this merchant AND are not a Thanx admin impersonating, so impersonated
  // sessions don't see merchant-owner reminders.
  const isThanxAdmin = useIsThanxAdmin()
  const isMerchantAdmin = !!isAdmin && !isThanxAdmin
  const shouldCheck = flag === 'nag' && isMerchantAdmin && !!merchant?.id

  const { data, isLoading } =
    credentialSharingAgreementApi.useGetCredentialSharingAgreementStatusQuery(
      merchant?.id,
      {
        skip: !shouldCheck,
      }
    )

  const [isDismissed, dismiss] = useCredentialSharingNagDismissal()
  const [setAuthority] = useSetCredentialSharingAuthorityMutation()

  // The backend `show` already encodes mode === 'nag' && authority !== 'granted'
  // && obligations non-empty. We re-assert the client-side gates (flag + merchant
  // admin) so an impersonated session never surfaces the nag. Gate on `isLoading`
  // (first load only), NOT `isFetching` — a background refetch (arg change, cache
  // invalidation, refetch-on-focus) sets `isFetching` true, which would flip
  // `show` false and unmount the guard mid-interaction (flicker + lost modal
  // state). Stale-but-present `data.show` is a safe signal.
  const show = flag === 'nag' && isMerchantAdmin && !isLoading && !!data?.show
  const showModal = show && !isDismissed

  // "Let Thanx handle it" is a single terminal action: open the LPA sign flow,
  // then dismiss for the day (the persistent banner stays as a re-entry point).
  // Open BEFORE dismissing so the navigation isn't ordered behind the state
  // change. (noopener makes window.open return null even on success, so we can't
  // gate the dismiss on its return value.)
  const handleGrantAuthority = () => {
    const target = getLpaTarget()
    openExternal(
      target.kind === 'url' ? target.url : buildLpaSupportMailto(merchant)
    )
    dismiss()
  }

  // Per-obligation self-serve action — must NOT dismiss. In the self-serve view
  // every obligation's "Open App Store Connect" button calls this; dismissing
  // here would close the modal on the first click and strand a merchant who has
  // a second blocking obligation. Dismissal is owned by "Remind me later" and the
  // grant flow, so the merchant can action every obligation in one pass.
  const handleManageOnApple = () => {
    openExternal(APP_STORE_CONNECT_URL)
  }

  // Record the merchant's standing decision. Fire-and-forget: the mutation
  // invalidates the status query, so `authority`/`show` refresh on their own.
  // Guard on merchant id (the URL is merchant-scoped via the base query).
  const handleDecline = () => {
    if (!merchant?.id) return
    setAuthority({ merchantId: merchant.id, authority: 'declined' })
  }

  const handleReconsider = () => {
    if (!merchant?.id) return
    setAuthority({ merchantId: merchant.id, authority: 'pending' })
  }

  return {
    show,
    showModal,
    authority: data?.authority,
    obligations: data?.obligations ?? [],
    dismiss,
    handleGrantAuthority,
    handleManageOnApple,
    handleDecline,
    handleReconsider,
  }
}
